import type { ReactNode } from 'react';

interface SkillButtonProps {
  icon: ReactNode;
  cd: number;
  onFire: () => void;
  color: string;
  label: string;
  /** Inventory charges left. If 0, the button is dimmed and not tappable. */
  count?: number;
  /** 2v2: skill is combo/role-locked. Shows a lock overlay, not tappable. */
  locked?: boolean;
}

export function SkillButton({ icon, cd, onFire, color, label, count, locked }: SkillButtonProps) {
  const ready = cd <= 0;
  const outOfCharges = count != null && count <= 0;
  const tappable = ready && !outOfCharges && !locked;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
      <div
        className="skill-btn"
        data-cooldown={!ready || outOfCharges || locked}
        onClick={tappable ? onFire : undefined}
        style={{
          borderColor: tappable ? color : 'var(--hairline)',
          boxShadow: tappable
            ? `inset 0 1px 0 rgba(255,255,255,0.1), 0 4px 0 rgba(0,0,0,0.4), 0 0 18px ${color}88`
            : 'inset 0 1px 0 rgba(255,255,255,0.05), 0 4px 0 rgba(0,0,0,0.4)',
          opacity: outOfCharges || locked ? 0.5 : 1,
          cursor: tappable ? 'pointer' : 'not-allowed',
          position: 'relative',
        }}
      >
        {icon}
        {locked && ready && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(0,0,0,0.55)',
              borderRadius: 16,
              fontFamily: 'var(--f-display)',
              fontSize: 9,
              letterSpacing: '0.1em',
              color: 'var(--ink-dim)',
            }}
          >
            LOCKED
          </div>
        )}
        {!ready && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(0,0,0,0.65)',
              borderRadius: 16,
              fontFamily: 'var(--f-mono)',
              fontSize: 18,
              color: 'var(--ink)',
              fontWeight: 700,
            }}
          >
            {cd.toFixed(1)}
          </div>
        )}
        {count != null && (
          <div
            style={{
              position: 'absolute',
              top: -4,
              right: -4,
              minWidth: 22,
              height: 22,
              borderRadius: 11,
              padding: '0 6px',
              background: outOfCharges ? 'var(--c-red)' : color,
              color: outOfCharges ? '#fff' : 'var(--bg-deep)',
              fontFamily: 'var(--f-display)',
              fontSize: 11,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '2px solid var(--bg-deep)',
              boxShadow: outOfCharges ? '0 0 6px var(--c-red)' : `0 0 6px ${color}`,
            }}
          >
            {outOfCharges ? '0' : `×${count}`}
          </div>
        )}
      </div>
      <div
        style={{
          fontFamily: 'var(--f-display)',
          fontSize: 8,
          letterSpacing: '0.1em',
          color: tappable ? color : 'var(--ink-dim)',
        }}
      >
        {label}
      </div>
    </div>
  );
}
